import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Star, ChevronLeft, Sun, Moon, Compass, Sparkles, Loader2, MapPin, Calendar, Clock, ArrowRight, Info, User } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/components/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sign colors and info
const SIGN_COLORS = {
  'Bélier': '#FF4444', 'Taureau': '#4CAF50', 'Gémeaux': '#FFEB3B', 'Cancer': '#90CAF9',
  'Lion': '#FF9800', 'Vierge': '#8BC34A', 'Balance': '#E91E63', 'Scorpion': '#9C27B0',
  'Sagittaire': '#673AB7', 'Capricorne': '#607D8B', 'Verseau': '#00BCD4', 'Poissons': '#3F51B5'
};

const PillarCard = ({ title, subtitle, icon: Icon, sign, house, degree, color, keywords, question, isMain }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`
      relative overflow-hidden rounded-2xl p-6
      ${isMain ? 'bg-white border-2 border-sky-400 shadow-lg shadow-sky-100' : 'bg-white border border-sky-200'}
    `}
  >
    <div className="relative">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="p-3 rounded-xl"
            style={{ backgroundColor: `${color}15`, border: `1px solid ${color}40` }}
          >
            <Icon className="h-6 w-6" style={{ color }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
        </div>
      </div>
      
      {/* Sign */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-3xl font-bold" style={{ color }}>{sign?.symbol}</span>
          <div>
            <span className="text-xl font-bold text-gray-900">{sign?.name}</span>
            <span className="text-gray-500 ml-2">à {degree}°</span>
          </div>
        </div>
        {house > 0 && (
          <p className="text-sm text-gray-500">Maison {house}</p>
        )}
      </div>
      
      {/* Element & Quality */}
      <div className="flex gap-2 mb-4">
        <span className="px-2 py-1 text-xs rounded-full bg-sky-50 text-sky-700 border border-sky-200">
          {sign?.element}
        </span>
        <span className="px-2 py-1 text-xs rounded-full bg-gray-50 text-gray-600 border border-gray-200">
          {sign?.quality}
        </span>
      </div>
      
      {/* Keywords */}
      <div className="flex flex-wrap gap-2 mb-4">
        {keywords?.slice(0, 4).map((kw, i) => (
          <span key={i} className="px-2 py-1 text-xs rounded-full border border-gray-200 text-gray-600">
            {kw}
          </span>
        ))}
      </div>
      
      {/* Question */}
      {question && (
        <div className="p-3 bg-sky-50 rounded-lg border border-sky-200">
          <p className="text-sm text-sky-700 italic">"{question}"</p>
        </div>
      )}
    </div>
  </motion.div>
);

const AstrozodiacBigThreePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    birthDate: '',
    birthTime: '',
    birthCity: 'Paris',
    birthCountry: 'FR'
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [interpretation, setInterpretation] = useState(null);
  const [loadingInterpretation, setLoadingInterpretation] = useState(false);
  const [profilePreFilled, setProfilePreFilled] = useState(false);

  // Auto-fill from user profile
  useEffect(() => {
    if (isAuthenticated && user && !profilePreFilled) {
      const newFormData = { ...formData };
      let hasPreFilled = false;
      
      if (user.first_name || user.name) {
        newFormData.firstName = user.first_name || user.name;
        hasPreFilled = true;
      }
      if (user.birth_date) {
        newFormData.birthDate = user.birth_date;
        hasPreFilled = true;
      }
      if (user.birth_time) {
        newFormData.birthTime = user.birth_time;
        hasPreFilled = true;
      }
      if (user.birth_city) {
        newFormData.birthCity = user.birth_city;
        hasPreFilled = true;
      }
      
      if (hasPreFilled) {
        setFormData(newFormData);
        setProfilePreFilled(true);
        toast.success('Données pré-remplies depuis votre profil !', {
          description: 'Modifiez si nécessaire'
        });
      }
    }
  }, [isAuthenticated, user, profilePreFilled]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.birthDate || !formData.birthCity) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setResult(null);
    setInterpretation(null);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/big-three`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          birth_date: formData.birthDate,
          birth_time: formData.birthTime || null,
          birth_city: formData.birthCity,
          birth_country: formData.birthCountry
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data);
        toast.success('Analyse calculée !');
      } else {
        toast.error(data.detail || 'Erreur de calcul');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  const getInterpretation = async () => {
    if (!result) return;
    
    setLoadingInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/interpret/big-three`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chart_data: result,
          focus: 'big_three',
          first_name: formData.firstName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setInterpretation(data.interpretation);
        toast.success('Interprétation générée !');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingInterpretation(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50">
      {/* Subtle decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-amber-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-sky-200/30 rounded-full blur-3xl" />
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-sky-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link 
            to="/astrozodiac" 
            className="flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Sun className="h-5 w-5 text-amber-500" />
            <span className="font-bold text-gray-900">Les 3 Piliers</span>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Form Section */}
        {!result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto"
          >
            <div className="text-center mb-8">
              <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200 mb-4">
                <Sun className="h-10 w-10 text-amber-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Découvre tes 3 Piliers</h1>
              <p className="text-gray-600">Soleil, Lune et Ascendant : l'essence de qui tu es</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5 bg-white p-6 rounded-2xl border border-sky-200 shadow-sm">
              <div>
                <Label className="text-gray-700">Prénom *</Label>
                <Input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="Ton prénom"
                  className="mt-1 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-xl focus:border-sky-400 focus:ring-sky-400"
                  data-testid="input-firstname"
                />
              </div>
              
              <div>
                <Label className="text-gray-700">Date de naissance *</Label>
                <Input
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl focus:border-sky-400 focus:ring-sky-400"
                  data-testid="input-birthdate"
                />
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Heure de naissance (optionnel)
                </Label>
                <Input
                  type="time"
                  value={formData.birthTime}
                  onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl focus:border-sky-400 focus:ring-sky-400"
                  data-testid="input-birthtime"
                />
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  L'Ascendant nécessite l'heure exacte
                </p>
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Ville de naissance *
                </Label>
                <Input
                  type="text"
                  value={formData.birthCity}
                  onChange={(e) => setFormData({ ...formData, birthCity: e.target.value })}
                  placeholder="Paris"
                  className="mt-1 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-xl focus:border-sky-400 focus:ring-sky-400"
                  data-testid="input-city"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-400 hover:to-cyan-400 text-white font-semibold rounded-xl py-6"
                data-testid="calculate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Calcul en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Calculer mes 3 Piliers
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        )}
        
        {/* Results Section */}
        {result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            {/* Header */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Les 3 Piliers de {formData.firstName}
              </h2>
              {!result.has_birth_time && (
                <p className="text-amber-600 text-sm flex items-center justify-center gap-2">
                  <Info className="h-4 w-4" />
                  Ascendant approximatif - ajoute ton heure de naissance pour plus de précision
                </p>
              )}
            </div>
            
            {/* Big Three Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <PillarCard
                title="Soleil"
                subtitle="Ton identité consciente"
                icon={Sun}
                sign={result.big_three?.sun?.sign}
                house={result.big_three?.sun?.house}
                degree={result.big_three?.sun?.degree}
                color="#F59E0B"
                keywords={result.big_three?.sun?.sign?.keywords}
                question="Qui suis-je vraiment ?"
                isMain={true}
              />
              
              <PillarCard
                title="Lune"
                subtitle="Ton monde émotionnel"
                icon={Moon}
                sign={result.big_three?.moon?.sign}
                house={result.big_three?.moon?.house}
                degree={result.big_three?.moon?.degree}
                color="#64748B"
                keywords={result.big_three?.moon?.sign?.keywords}
                question="De quoi ai-je besoin ?"
                isMain={false}
              />
              
              <PillarCard
                title="Ascendant"
                subtitle="Ton masque social"
                icon={Compass}
                sign={result.big_three?.ascendant?.sign}
                house={1}
                degree={result.big_three?.ascendant?.degree}
                color="#0EA5E9"
                keywords={result.big_three?.ascendant?.sign?.keywords}
                question="Comment j'aborde la vie ?"
                isMain={false}
              />
            </div>
            
            {/* AI Interpretation */}
            <div className="bg-white rounded-2xl border border-sky-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-sky-500" />
                Interprétation IA personnalisée
              </h3>
              
              {interpretation ? (
                <div className="prose prose-sm max-w-none text-gray-700">
                  <ReactMarkdown>{interpretation}</ReactMarkdown>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 text-sm mb-4">
                    Obtiens une analyse approfondie de ton trio essentiel
                  </p>
                  <Button
                    onClick={getInterpretation}
                    disabled={loadingInterpretation}
                    className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-400 hover:to-cyan-400 text-white rounded-xl"
                    data-testid="get-interpretation-btn"
                  >
                    {loadingInterpretation ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Analyse en cours...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-5 w-5" />
                        Obtenir l'interprétation IA
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => { setResult(null); setInterpretation(null); }}
                className="border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl"
                data-testid="new-calc-btn"
              >
                Nouveau calcul
              </Button>
              <Button
                onClick={() => navigate('/astrozodiac/planets')}
                className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white rounded-xl"
                data-testid="go-planets-btn"
              >
                Voir mes planètes
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default AstrozodiacBigThreePage;
